import type { User } from "oidc-client-ts";
import type { AuthContextProps } from "react-oidc-context";

import { createMockJwtToken } from "@/lib/mockJwt";

/**
 * A fake signed-in OIDC user carrying a decodable mock JWT. Build it ONCE per
 * mount (useState initialiser / module level): a fresh object each render
 * would change `user` identity and churn every `useAuth()` / `useJwt()`
 * consumer (render-stability).
 */
export const createMockUser = ({
  nowMs,
  sub,
}: {
  nowMs: number;
  sub: string;
}): User => {
  const nowSeconds = Math.floor(nowMs / 1000);
  return {
    access_token: createMockJwtToken({ nowMs, sub }),
    session_state: "mock-session",
    token_type: "Bearer",
    profile: {
      sub,
      iss: "mock-issuer",
      aud: "webapp-customer",
      exp: nowSeconds + 3600,
      iat: nowSeconds,
    },
    state: "mock-state",
    expires_in: 3600,
    expired: false,
    scopes: ["openid", "profile"],
    toStorageString: () => "mock-storage-string",
  } as User;
};

const noop = () => {};
const noopAsync = async () => {};

/**
 * A complete `react-oidc-context` value for a fake, always-authenticated
 * session, provided straight through `AuthContext.Provider` so the auth layer
 * itself creates no `UserManager`: no stored-token load, no silent-renew
 * timers, no metadata discovery. Every action is a no-op. This covers the
 * React auth layer only; the rest of mock mode's no-real-backend guarantee
 * lives in MockModeGate (wipes `oidc.*`; its unhandled-request rule fails any
 * call that reaches the BFF or Keycloak host), baseQuery (no stored token, no
 * 401 renewal) and the MSW handlers on the BFF origin (an unmocked path gets
 * the 501 catch-all, mocks/bffBase.ts).
 */
export const createMockAuthContextValue = (user: User): AuthContextProps =>
  ({
    isLoading: false,
    isAuthenticated: true,
    user,
    activeNavigator: undefined,
    error: undefined,
    settings: {},
    signinRedirect: noopAsync,
    signinSilent: async () => null,
    signinPopup: async () => user,
    signinResourceOwnerCredentials: async () => user,
    signoutRedirect: noopAsync,
    signoutPopup: noopAsync,
    signoutSilent: noopAsync,
    removeUser: noopAsync,
    clearStaleState: noopAsync,
    revokeTokens: noopAsync,
    startSilentRenew: noop,
    stopSilentRenew: noop,
    querySessionStatus: async () => null,
    events: {
      addAccessTokenExpiring: noop,
      removeAccessTokenExpiring: noop,
      addAccessTokenExpired: noop,
      removeAccessTokenExpired: noop,
      addSilentRenewError: noop,
      removeSilentRenewError: noop,
      addUserLoaded: noop,
      removeUserLoaded: noop,
      addUserUnloaded: noop,
      removeUserUnloaded: noop,
      addUserSignedIn: noop,
      removeUserSignedIn: noop,
      addUserSignedOut: noop,
      removeUserSignedOut: noop,
      addUserSessionChanged: noop,
      removeUserSessionChanged: noop,
    },
  }) as unknown as AuthContextProps;
